import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Ticket, XIcon } from "lucide-react";
import CustomPaginationComponent from "@/components/common/custom-pagination-component";
import useProfessionals from "@/features/professionals/hooks/use-professionals";
import { ProfessionalCard } from "@/features/professionals/ui/professional-card";
import VaulDrawer from "@/components/ui/drawer";
import AddEditProfessionalsForm from "@/features/professionals/ui/professional-form";
import ProfessionalFiltersOnly from "@/features/professionals/ui/professional-filters-only";
import { SearchBar } from "@/components/common/search-bar";
import { useHasAccess } from "@/components/common/has-access";
import { ContentLayout } from "@/components/common/content-layout";

export default function Professionals() {
  const [searchParams] = useSearchParams();
  const [showProfessionalFormDrawer, setShowProfessionalFormDrawer] =
    useState(false);
  // The drawer is how a professional is VIEWED, not only edited, so it must
  // open for everyone who can see the list. Only what it lets you do depends
  // on Edit.
  const canWrite = useHasAccess("write");
  const [selectedProfessional, setSelectedProfessional] = useState<any>(null);
  const initialPage = parseInt(searchParams.get("page") || "0", 10);
  const [_page, setCurrentPage] = useState(initialPage);
  const navigate = useNavigate();

  // Get filter values from URL params
  const search = searchParams.get("search") || "";
  const status = searchParams.get("status") || undefined;
  const job_commitment = searchParams.get("job_commitment") || undefined;
  const type = searchParams.get("type") || undefined;
  const qualification =
    searchParams.get("qualification")?.split(",") || undefined;

  // Fetch professionals with filters
  const { data, error, isFetching, isLoading } = useProfessionals(
    _page,
    search,
    status,
    job_commitment,
    qualification,
    type
  );

  const professionals = data?.professionals || [];
  const supervisors = data?.supervisors || [];
  const leads = data?.leads || [];
  const currentPage = data?.currentPage || 0;
  const lastPage = data ? data.lastPage + 1 : 0;

  useEffect(() => {
    if (selectedProfessional) {
      const updated = professionals.find((p) => p.id === selectedProfessional.id);
      if (updated) setSelectedProfessional(updated);
    }
  }, [professionals]);

  const handlePageChange = (page: number) => {
    if (page >= 0 && page < lastPage) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", page.toString());
      navigate(`?${newParams.toString()}`, { replace: true });
      setCurrentPage(page);
    }
  };

  return (
    <ContentLayout
      header={
        <div className="flex flex-wrap items-center gap-4">
          <ProfessionalFiltersOnly />
          <div className="flex-1 min-w-[200px]">
            <SearchBar
              placeholder="Search Professionals..."
              urlParamStrategy="prefix"
              clearButton={{
                enabled: true,
                position: "inside",
                ariaLabel: "Clear search",
                icon: <XIcon className="w-4 h-4" />
              }}
              // fieldSelector={{
              //   enabled: true,
              //   fields: [
              //     { value: "name", label: "Name" },
              //     { value: "email", label: "Email" },
              //     { value: "phone", label: "Phone" }
              //   ],
              //   allowAll: true
              // }}
            />
          </div>
        </div>
      }
      data={professionals}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      errorMessage="Failed to load professionals"
      emptyState={{
        message: "No professionals found matching your criteria",
        icon: <Ticket className="h-8 w-8 text-muted-foreground" />
      }}
    >
      <ProfessionalCard
        professionals={professionals}
        setShowProfessionalDrawer={setShowProfessionalFormDrawer}
        setSelectedProfessional={setSelectedProfessional}
      />
      {professionals.length > 0 && (
        <CustomPaginationComponent
          currentPage={currentPage}
          lastPage={lastPage}
          onPageChange={handlePageChange}
        />
      )}
      <VaulDrawer
        drawerDirection="right"
        isOpen={showProfessionalFormDrawer}
        onOpenChange={setShowProfessionalFormDrawer}
        triggerChildren={null}
        contentChildren={
          <AddEditProfessionalsForm
            readOnly={!canWrite}
            editMode={!!selectedProfessional}
            onClose={() => setShowProfessionalFormDrawer(false)}
            initialData={selectedProfessional}
            supervisors={supervisors}
            leads={leads}
          />
        }
      />
    </ContentLayout>
  );
}
